import { TENANT_TAX_DOC_ID, TenantTaxDocSchema, type TenantTaxDoc } from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * SST / tax-invoice details — `tenants/{tid}/private/tax`.
 *
 * A subcollection rather than a field on the tenant profile, because
 * `firestore.rules` grants `match /tenants/{tid} { allow read: if true }` so the
 * unauthenticated customer PWA can resolve a tenant from a hostname and paint
 * its shell. Anything on that document is public.
 *
 * This block is not publishable. `companyRegistration` holds the SSM number —
 * or, for a sole proprietorship, the owner's **NRIC**, which is the usual shape
 * for a small Malaysian carwash — and `registeredAddress` is that person's home
 * address more often than not. Tenant ids are not secret either: `slugToTenantId`
 * and the public `outletDirectory` both hand them out, so "nobody knows the id"
 * was never a defence.
 *
 * Read access is owner/manager — deliberately the same bar as `invoices`, since
 * this is the header printed on them. Writes go through the `setTenantTaxConfig`
 * callable, which validates the SST number and clears the whole document on
 * de-registration; `firestore.rules` denies client writes outright so that
 * validation cannot be bypassed.
 */
export type { TenantTaxDoc };

export { TENANT_TAX_DOC_ID };

/**
 * The collection segment is written out LITERALLY here, not interpolated from
 * `TENANT_PRIVATE_COLLECTION`, and that is deliberate.
 *
 * `pnpm seed:coverage` fails the push when an app reads a collection no seeder
 * writes — the gate that exists because an unseeded collection does not just
 * hide a screen, it disables code paths. It finds collections by resolving the
 * `path:` string in repository declarations, so a path ending in `${...}`
 * reduces to a segment it cannot name, and this collection became invisible to
 * it: the gate reported "every collection an app reads has a fixture" while
 * knowing nothing about this one.
 *
 * A checker that silently skips what it cannot parse is the failure mode this
 * repo keeps rediscovering, so the literal wins. `tenant-tax-repo.test.ts` pins
 * the literal to `TENANT_PRIVATE_COLLECTION` and to the `match /private/{docId}`
 * rule, so the three cannot drift apart instead. That test reads this file as
 * TEXT rather than importing it — importing pulls in `_base.ts` → `firebase.ts`
 * → `env.ts`, which validates browser env vars at import time and throws under
 * vitest, which is why no other repository here has a unit test.
 */
export function tenantTaxRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/private`,
    schema: TenantTaxDocSchema,
    name: `tenants/${tenantId}/private`,
  });
}
